import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import EhrIcon from '@/components/ui/ehr-icon';
import {
  statusIconColors,
  type statusIconVariants,
} from '@/components/ui/session-status/status.variants';
import { useUpcomingSessionsList } from '@/features/upcomingSessionTab/hooks/useUpcomingSessionsList';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useSessionDateDisplay } from '@/hooks/useSessionDateDisplay';
import { cn } from '@/lib/cn';
import { enumToDescription } from '@/utils/enumToDescription';
import { Avatar, AvatarFallback } from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { SessionStatus } from '@heidi/schema/ml-scribe';
import { type UpcomingSession } from '@heidi/session';
import { useAtomValue } from 'jotai';
import { Circle, Unlink } from 'lucide-react';
import { useRouter } from 'next/router';
import { Fragment, useMemo } from 'react';

interface UpcomingSessionItemProps {
  session: UpcomingSession;
  index: number;
  onClick: () => void;
}

export const UpcomingSessionItem = ({
  session,
  index,
  onClick,
}: UpcomingSessionItemProps) => {
  const { t } = useTranslations();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const { upcomingSessions } = useUpcomingSessionsList();
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const { isTogetherOrHigherTier } = useQueryUserTier();
  const { displaySessionTime, displayFormattedDate } = useSessionDateDisplay();
  const scheduleDate = useAtomValue(scheduleDateAtom);
  const router = useRouter();
  const prevSession = useMemo(
    () => upcomingSessions?.at(index - 1),
    [upcomingSessions, index]
  );
  const prevDate = useMemo(
    () =>
      prevSession ? displayFormattedDate(prevSession.created_time_local, true) : null,
    [prevSession, displayFormattedDate]
  );

  const isSessionLinked = useMemo(
    () => session.isFromEhr || !!session.patient_profile,
    [session.isFromEhr, session.patient_profile]
  );

  const patientInitials = useMemo(() => {
    const name = session.displayName?.trim();
    if (!name) {
      return undefined;
    }

    const parts = name.split(/\s+/);
    if (parts.length === 2) {
      const firstInitial = parts.at(0)?.charAt(0) ?? '';
      const secondInitial = parts.at(1)?.charAt(0) ?? '';
      return `${firstInitial}${secondInitial}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }, [session.displayName]);

  const currentDate = useMemo(
    () => displayFormattedDate(session.created_time_local, true),
    [session.created_time_local, displayFormattedDate]
  );
  const showDateHeader = useMemo(
    () => index === 0 || currentDate !== prevDate,
    [index, currentDate, prevDate]
  );

  const isActiveSession =
    session.session_id && router.asPath.includes(session.session_id);

  return (
    <Fragment>
      {showDateHeader && !scheduleDate && (
        <div className="p-1.5">
          <TypographyCaptionBold className="text-text-secondary">
            {currentDate}
          </TypographyCaptionBold>
        </div>
      )}
      <Button
        type="button"
        variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
        size="lg"
        data-active={isSidebarPanelEnabled ? !!isActiveSession : undefined}
        className={cn(
          'group flex w-full cursor-pointer justify-start rounded-sm px-1.5 py-1',
          !isSidebarPanelEnabled &&
            cn(
              'hover:bg-surface-hover',
              isActiveSession && 'bg-surface-selected hover:bg-surface-selected-hover'
            )
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <Avatar
            className={cn(
              'size-6 border-[0.75px]',
              isSessionLinked
                ? 'border-border-selected text-text-primary'
                : 'border-text-tertiary text-text-tertiary'
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <AvatarFallback
              className={cn(
                'flex items-center justify-center bg-transparent',
                !isSessionLinked && 'p-[5px]'
              )}
            >
              {isSessionLinked ? (
                <TypographyCaption className="leading-none">
                  {patientInitials}
                </TypographyCaption>
              ) : (
                <Unlink />
              )}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start overflow-hidden">
            <div className="flex items-center gap-1">
              {session.isFromEhr && (
                <div className="size-[12px]">
                  <EhrIcon
                    ehrProvider={integrationEhrProvider}
                    iconSize={12}
                    iconClassName="text-text-secondary"
                  />
                </div>
              )}
              <TypographyCaption>
                {session.displayName || t('shared.UntitledSession')}
              </TypographyCaption>
            </div>
            <div className="flex items-center gap-1">
              {isTogetherOrHigherTier && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="size-1.5">
                      <Circle
                        fill="currentColor"
                        className={cn(
                          statusIconColors[
                            SessionStatus.EMPTY.toLowerCase() as keyof typeof statusIconVariants
                          ],
                          'max-h-full max-w-full'
                        )}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <TooltipArrow />
                    <TypographyCaption>
                      {t(
                        `sessionStatus.${enumToDescription[SessionStatus.EMPTY]}` as TranslationKey
                      )}
                    </TypographyCaption>
                  </TooltipContent>
                </Tooltip>
              )}
              <TypographyCaption>
                {displaySessionTime(session.created_time_local, true)}
              </TypographyCaption>
            </div>
          </div>
        </div>
      </Button>
    </Fragment>
  );
};
